import React, { useEffect, useState } from 'react';
import { FaArrowUp, FaChevronDown, FaChevronUp } from 'react-icons/fa';
import { useLocale } from '../../../contexts/LocaleContext.jsx';
import { useBoostQuote } from '../../../hooks/useBoostQuote.js';
import BoostAnimationPicker from './BoostAnimationPicker.jsx';
import '../../../styles/BoostDesk.css';
import '../../../styles/StreamCta.css';

/** Payment error codes with their own message; anything else reads as generic. */
const PAY_ERRORS = new Set([
  'card_declined', 'card_authentication_required', 'boost_refunded', 'boost_pending',
  'boost_next_up_locked', 'boost_top_spot', 'boost_not_queued', 'boost_unavailable', 'show_not_live',
]);

/** The catalog key under stream.boost.desk.payError for a payment error code (camelCased). */
export const boostPayError = (code) => (PAY_ERRORS.has(code) ? code.replace(/_([a-z])/g, (_, c) => c.toUpperCase()) : 'generic');

/** Quick-add amounts, the same on the song page (decided on ENG-677/709). */
export const BOOST_QUICK_ADDS = [5, 10, 25, 50];

/**
 * The boost desk (mockup 06, ENG-677): three ways to price a boost of the
 * chosen song — BY SPOT (a stepper), BY AMOUNT (quick adds) and TOP SPOT —
 * kept in step by the quote endpoint (ENG-678), and the Boost button. It
 * opens one spot up from the song's own, already priced. The queue changing
 * (`queueKey`) re-quotes, so the price shown is always for the queue as it is.
 *
 * `onConfirm(quote)` pays for the boost (ENG-679/680); without it the button
 * shows the price but cannot be pressed. `payState` (idle, paying,
 * redirecting, failed) and `payError` (a stable code) report the payment: the
 * button is off while one is under way, so a double click pays once.
 *
 * `showAnimation` (signed in) adds the listener's boost animation (ENG-814):
 * the one everyone sees when they boost, a preview of it on a sample card,
 * and their other owned ones to equip (BoostAnimationPicker).
 */
const BoostDesk = ({
  bandId,
  entry,
  queueKey,
  onConfirm,
  payState = 'idle',
  payError = null,
  showAnimation = false,
  animationPreviewTiming = null,
}) => {
  const { t, format } = useLocale();
  const [request, setRequest] = useState({});
  const { quote, error, loading } = useBoostQuote(bandId, entry?.entryId, request, queueKey);

  // A new song starts over at its default spot.
  useEffect(() => { setRequest({}); }, [entry?.entryId]);

  if (!entry) return null;

  const spot = quote?.targetSpot ?? null;
  const amount = quote?.amountToAdd ?? 0;
  const current = quote?.currentSpot ?? null;
  const usd = (value) => format.usd(value, { cents: 'auto' });
  const canGoUp = spot != null && spot > 1;
  const canGoDown = spot != null && current != null && spot + 1 < current;
  const ready = Boolean(quote) && !loading && !error && amount > 0;
  const busy = payState === 'paying' || payState === 'redirecting';

  let status = '';
  if (busy) status = t(payState === 'redirecting' ? 'stream.boost.desk.redirecting' : 'stream.boost.desk.paying');
  else if (payError) status = t(`stream.boost.desk.payError.${boostPayError(payError)}`);
  else if (error) status = t('stream.boost.desk.error');
  else if (ready) status = t('stream.boost.desk.summary', { title: entry.title, spot: format.number(spot), amount: usd(amount) });

  return (
    <div className="boost-desk" data-testid="boost-desk" role="group" aria-label={t('stream.boost.desk.label', { title: entry.title })}>
      <div className="boost-desk-controls">
        <div className="boost-desk-panel">
          <span className="boost-desk-heading">{t('stream.boost.desk.bySpot')}</span>
          <div className="boost-desk-spot">
            <span className="boost-desk-big" data-testid="boost-desk-spot">
              {spot != null ? t('stream.boost.desk.spotValue', { spot: format.number(spot) }) : '–'}
            </span>
            <div className="boost-desk-stepper">
              <button type="button" onClick={() => setRequest({ target: spot - 1 })} disabled={!canGoUp} aria-label={t('stream.boost.desk.up')}>
                <FaChevronUp aria-hidden="true" />
              </button>
              <button type="button" onClick={() => setRequest({ target: spot + 1 })} disabled={!canGoDown} aria-label={t('stream.boost.desk.down')}>
                <FaChevronDown aria-hidden="true" />
              </button>
            </div>
          </div>
        </div>
        <div className="boost-desk-panel">
          <span className="boost-desk-heading">{t('stream.boost.desk.byAmount')}</span>
          <div className="boost-desk-amount">
            <span className="boost-desk-big" data-testid="boost-desk-amount">{usd(amount)}</span>
            <div className="boost-desk-adds">
              {BOOST_QUICK_ADDS.map((add) => (
                <button
                  key={add}
                  type="button"
                  className="boost-desk-add"
                  onClick={() => setRequest({ amount: amount + add })}
                  aria-label={t('stream.boost.desk.addLabel', { amount: usd(add) })}
                >
                  {t('stream.boost.desk.add', { amount: usd(add) })}
                </button>
              ))}
            </div>
          </div>
        </div>
        <button
          type="button"
          className="boost-desk-top"
          data-testid="boost-desk-top"
          onClick={() => setRequest({ target: 1 })}
          disabled={!quote || current === 1}
        >
          <span className="boost-desk-heading">{t('stream.boost.desk.topSpot')}</span>
          <span className="boost-desk-top-price">{quote ? usd(quote.topSpotPrice) : '–'}</span>
          <span className="boost-desk-top-hint">{t('stream.boost.desk.tapForTop')}</span>
        </button>
      </div>

      <button
        type="button"
        className="boost-desk-go stream-cta"
        data-testid="boost-desk-go"
        disabled={!ready || !onConfirm || busy}
        aria-busy={busy || undefined}
        onClick={() => onConfirm?.(quote)}
      >
        <FaArrowUp className="boost-desk-go-icon" aria-hidden="true" />
        <span className="boost-desk-go-label">{t('stream.boost.desk.boost')}</span>
        <span className="boost-desk-go-hint">
          {amount > 0 ? usd(amount) : t('stream.boost.desk.addAnAmount')}
        </span>
      </button>

      <p className="boost-desk-status" role="status" aria-live="polite">
        {status}
      </p>

      {showAnimation && <BoostAnimationPicker previewTiming={animationPreviewTiming} />}
    </div>
  );
};

export default BoostDesk;
